import { FieldSolver2D, throwIfErrors } from './field_solver.js';
import { Dielectric, Conductor, Mesher, DEFAULT_GRID_N } from './mesher.js';
import { halfDomainSymmetry } from './geometry_symmetry.js';

// Auto domain width of a full-pour GCPW in slot apertures (trace span + both gaps).
// Above the pours the field is that of the aperture over a ground plane, and the open
// side walls truncate it: the Z0 error falls as (aperture / width)^2.
const GCPW_APERTURE_WIDTHS = 7;

class MicrostripSolver extends FieldSolver2D {
    constructor(options) {
        super();

        // Validate parameters before proceeding
        this._validate_parameters(options);

        // Store parameters
        this.h = options.substrate_height;
        this.w = options.trace_width;
        this.t = options.trace_thickness;
        this.t_gnd = options.gnd_thickness ?? 35e-6;
        this.er = options.epsilon_r;
        this.er_top = options.epsilon_r_top ?? 1;
        this.tand_top = options.tan_delta_top ?? 0;
        this.tan_delta = options.tan_delta ?? 0.02;
        this.sigma_cond = options.sigma_cond ?? 5.8e7;
        // Numerical backend: 'rectilinear' (FDM, default) or 'triangular' (FEM).
        this.mesh_backend = options.mesh_backend ?? 'rectilinear';

        // Differential mode parameters
        this.trace_spacing = options.trace_spacing ?? null;
        this.is_differential = (this.trace_spacing !== null && this.trace_spacing > 0);

        this.gnd_cut_width = options.gnd_cut_width ?? 0.0;
        this.gnd_cut_sub_h = options.gnd_cut_sub_h ?? 0.0;
        this.top_diel_h = options.top_diel_h ?? 0.0;
        this.top_diel_er = options.top_diel_er ?? 1.0;
        this.top_diel_tand = options.top_diel_tand ?? 0.0;

        // Coplanar ground options (from GCPW)
        this.use_coplanar_gnd = options.use_coplanar_gnd ?? false;
        this.gap = options.gap ?? 0; // Gap from signal to top ground
        this.via_gap = options.via_gap ?? 0; // Gap from ground edge to via
        this.use_vias = options.use_vias ?? false; // Enable via generation
        // Coplanar ground width, measured outward from the gap edge. null ('full')
        // runs the grounds to the domain wall with the via fence from via_gap to
        // the wall. A finite width puts the via fence via_gap inside both ground
        // edges, so its width is coplanar_gnd_width - 2 * via_gap (must be > 0).
        const gnd_width = options.coplanar_gnd_width;
        this.coplanar_gnd_width = (gnd_width == null || gnd_width === 'full') ? null : gnd_width;

        // Enclosure options
        this.enclosure_width = options.enclosure_width ?? null;
        this.enclosure_width = this.enclosure_width === "auto" ? null : this.enclosure_width;
        this.enclosure_height = options.enclosure_height ?? null;
        this.enclosure_height = this.enclosure_height === "auto" ? null : this.enclosure_height;

        // Solder Mask Parameters
        this.use_sm = options.use_sm ?? false;
        this.sm_t_sub = options.sm_t_sub ?? 20e-6;
        this.sm_t_trace = options.sm_t_trace ?? 20e-6;
        this.sm_t_side = options.sm_t_side ?? 20e-6;
        this.sm_er = options.sm_er ?? 3.5;
        this.sm_tand = options.sm_tand ?? 0.02;

        this.freq = options.freq ?? 1e9;
        this.nx = options.nx ?? DEFAULT_GRID_N;
        this.ny = options.ny ?? DEFAULT_GRID_N;

        // Surface roughness parameter (RMS roughness in meters)
        this.rq = options.rq ?? 0;

        // Surface plating (single layer on top of bulk)
        this.plating = options.plating ?? null;

        this.boundaries = options.boundaries ?? ["open", "open", "open", "gnd"];

        // Determine if side grounds are present based on boundaries. Must be set
        // BEFORE the domain sizing below reads it (it used to be assigned after,
        // so the enclosure never got the +2·t_gnd side-wall widening and the walls
        // ate into the specified inner width).
        this.has_side_gnd = (this.boundaries[0] === "gnd" || this.boundaries[1] === "gnd");

        // Domain sizing
        if (this.enclosure_width !== null) {
            // Use explicit enclosure width
            // If side ground is enabled, this is the distance between inner walls
            // Otherwise, it's the total domain width
            if (this.has_side_gnd) {
                // enclosure_width is the air box size (inner wall to inner wall)
                // Add ground thickness on both sides
                this.domain_width = this.enclosure_width + 2 * this.t_gnd;
            } else {
                this.domain_width = this.enclosure_width;
            }
        } else if (this.is_differential) {
            // For differential, span includes both traces and spacing
            const trace_span = 2 * this.w + this.trace_spacing;
            if (this.use_coplanar_gnd) {
                this.domain_width = this._coplanar_auto_domain_width(trace_span);
            } else {
                this.domain_width = 2 * Math.max(trace_span * 4, this.h * 15);
            }
        } else {
            // Single-ended
            if (this.use_coplanar_gnd) {
                this.domain_width = this._coplanar_auto_domain_width(this.w);
            } else {
                this.domain_width = 2 * Math.max(this.w * 8, this.h * 15);
            }
        }

        // Calculate physical coordinates
        this._calculate_coordinates();

        // Calculate coplanar geometry if enabled
        if (this.use_coplanar_gnd) {
            this._calculate_coplanar_geometry_x();
        }

        // Build geometry lists
        const [dielectrics, conductors] = this._build_geometry_lists();
        this.dielectrics = dielectrics;
        this.conductors = conductors;

        // x=0 mirror symmetry: when the cross-section is mirror-symmetric about
        // x=0, the FDM grid covers only the right half (see Mesher half_x below).
        // Disabled with {symmetry: false}, also disables the triangular
        // backend's half domain (it reads tri_symmetry, see TriBackend.buildMesh).
        const symInfo = halfDomainSymmetry(this.conductors, this.dielectrics,
                                           this.domain_width, this.is_differential);
        if (options.symmetry === false) this.tri_symmetry = false;
        this.sym_half = this.mesh_backend !== 'triangular' && options.symmetry !== false && symInfo.ok;

        // Create mesher but don't generate mesh yet
        // Geometry is centered at x=0, so domain spans from -domain_width/2 to +domain_width/2
        // Y-coordinate system: bottom ground extends from -t_gnd to 0 (top surface at y=0)
        // Half-domain solve: the mesher still builds the symmetric full-domain grid
        // from the full rect lists and returns its x >= 0 half, so the half grid is
        // the exact right-half of the full one and this.conductors / this.dielectrics
        // (mask painting clips by bbox, conductor_id / plating / R_dc indices) stay full.
        this.mesher = new Mesher(
            this.domain_width, this.domain_height,
            this.nx, this.ny,
            this.conductors, this.dielectrics,
            true,  // symmetric
            -this.domain_width / 2,  // x_min
            this.domain_width / 2,   // x_max
            -this.t_gnd,             // y_min (bottom of bottom ground)
            this.domain_height,      // y_max (top of domain)
            this.sym_half            // half_x: return the x >= 0 half of the grid
        );

        // Mesh will be generated when needed
        this.mesh_generated = false;
    }

    _validate_parameters(options) {
        const errors = [];

        const isValidNumber = (val) => typeof val === 'number' && !isNaN(val) && isFinite(val);

        const checkPositive = (val, name) => {
            if (val === undefined || val === null) return;
            if (!isValidNumber(val)) {
                errors.push(`${name} must be a valid number (got ${val})`);
            } else if (val <= 0) {
                errors.push(`${name} must be positive (> 0), got ${val}`);
            }
        };

        const checkNonNegative = (val, name) => {
            if (val === undefined || val === null) return;
            if (!isValidNumber(val)) {
                errors.push(`${name} must be a valid number (got ${val})`);
            } else if (val < 0) {
                errors.push(`${name} must be non-negative (>= 0), got ${val}`);
            }
        };

        // Required positive parameters
        checkPositive(options.substrate_height, 'substrate_height');
        checkPositive(options.trace_width, 'trace_width');
        checkPositive(options.epsilon_r, 'epsilon_r');

        // Negative thickness embeds the trace in the substrate, but zero leaves no metal.
        if (!isValidNumber(options.trace_thickness)) {
            errors.push(`trace_thickness must be a valid number (got ${options.trace_thickness})`);
        } else if (options.trace_thickness === 0) {
            errors.push('trace_thickness must be nonzero');
        }

        checkNonNegative(options.freq, 'frequency');

        // Optional positive parameters
        if (options.gnd_thickness !== undefined) checkPositive(options.gnd_thickness, 'gnd_thickness');
        if (options.epsilon_r_top !== undefined) checkPositive(options.epsilon_r_top, 'epsilon_r_top');
        if (options.sigma_cond !== undefined) checkPositive(options.sigma_cond, 'sigma_cond');
        if (options.trace_spacing !== undefined && options.trace_spacing !== null && options.trace_spacing > 0) {
            checkPositive(options.trace_spacing, 'trace_spacing');
        }

        // Non-negative parameters
        checkNonNegative(options.tan_delta, 'tan_delta');
        checkNonNegative(options.gnd_cut_width, 'gnd_cut_width');
        checkNonNegative(options.gnd_cut_sub_h, 'gnd_cut_sub_h');
        checkNonNegative(options.top_diel_h, 'top_diel_h');
        checkNonNegative(options.top_diel_tand, 'top_diel_tand');
        // A zero gap makes the coplanar ground touch the trace
        if (options.use_coplanar_gnd) checkPositive(options.gap, 'gap');
        else checkNonNegative(options.gap, 'gap');
        checkNonNegative(options.via_gap, 'via_gap');
        checkNonNegative(options.rq, 'rq');

        // A finite coplanar ground must hold a via fence of positive width via_gap
        // inside both edges
        const gnd_width = options.coplanar_gnd_width;
        const finite_gnd = !!options.use_coplanar_gnd && gnd_width != null && gnd_width !== 'full';
        if (finite_gnd) {
            checkPositive(gnd_width, 'coplanar_gnd_width');
            const via_width = gnd_width - 2 * (options.via_gap || 0);
            if (via_width <= 0) {
                errors.push(`coplanar_gnd_width (${(gnd_width * 1e3).toFixed(3)} mm) must be > 2 * via_gap ` +
                    `(${(2 * (options.via_gap || 0) * 1e3).toFixed(3)} mm): the via thickness would not be positive`);
            }
        }

        if (options.trace_thickness <= -options.substrate_height) {
            errors.push("trace_thickness must be > -substrate_height");
        }

        // The trace rises from the substrate surface through the top dielectric
        // layer (if any) into the region above it. Equal heights put the top ground
        // flush on the trace's top face.
        if (options.enclosure_height != null && options.enclosure_height !== "auto"
            && options.trace_thickness >= options.enclosure_height + (options.top_diel_h ?? 0)) {
            errors.push("trace_thickness must be < enclosure_height + top_diel_h");
        }

        // Top dielectric epsilon_r should be positive if top dielectric is used
        if (options.top_diel_h > 0 && options.top_diel_er !== undefined) {
            checkPositive(options.top_diel_er, 'top_diel_er');
        }

        // Solder mask thicknesses: zero means that part of the mask is absent.
        if (options.use_sm) {
            checkNonNegative(options.sm_t_sub, 'sm_t_sub');
            checkNonNegative(options.sm_t_trace, 'sm_t_trace');
            checkNonNegative(options.sm_t_side, 'sm_t_side');
            checkPositive(options.sm_er, 'sm_er');
            checkNonNegative(options.sm_tand, 'sm_tand');
        }

        // Enclosure parameters
        if (options.enclosure_width !== undefined && options.enclosure_width !== "auto") {
            checkPositive(options.enclosure_width, 'enclosure_width');
        }
        if (options.enclosure_height !== undefined && options.enclosure_height !== "auto") {
            checkPositive(options.enclosure_height, 'enclosure_height');
        }

        // Grid size parameters
        if (options.nx !== undefined) {
            if (!Number.isInteger(options.nx) || options.nx <= 0) {
                errors.push(`nx must be a positive integer (got ${options.nx})`);
            }
        }
        if (options.ny !== undefined) {
            if (!Number.isInteger(options.ny) || options.ny <= 0) {
                errors.push(`ny must be a positive integer (got ${options.ny})`);
            }
        }

        // Check that active area fits in enclosure if enclosure is enabled
        const has_side_gnd_temp = (options.boundaries && (options.boundaries[0] === "gnd" || options.boundaries[1] === "gnd"));
        if (has_side_gnd_temp && options.enclosure_width !== undefined && options.enclosure_width !== "auto") {
            // Calculate active area width based on configuration
            let active_width = 0;
            const w = options.trace_width || 0;
            const trace_spacing = options.trace_spacing || 0;
            const is_differential = (trace_spacing !== null && trace_spacing > 0);

            if (is_differential) {
                const trace_span = 2 * w + trace_spacing;
                if (options.use_coplanar_gnd) {
                    const gap = options.gap || 0;
                    const gnd_extent = finite_gnd ? gnd_width : (options.via_gap || 0);
                    active_width = trace_span + 2 * (gap + gnd_extent);
                } else {
                    active_width = trace_span;
                }
            } else {
                if (options.use_coplanar_gnd) {
                    const gap = options.gap || 0;
                    const gnd_extent = finite_gnd ? gnd_width : (options.via_gap || 0);
                    active_width = w + 2 * (gap + gnd_extent);
                } else {
                    active_width = w;
                }
            }

            // enclosure_width is the inner width (between ground walls), so just check active area
            if (active_width > options.enclosure_width) {
                errors.push(`Active area width (${(active_width * 1000).toFixed(3)} mm) exceeds enclosure inner width (${(options.enclosure_width * 1000).toFixed(3)} mm)`);
            }
        }

        // If there are any errors, throw them
        throwIfErrors(errors);
    }

    // Auto domain width of a coplanar line whose traces span trace_span. Full-width
    // grounds reach the wall, so the domain only has to clear the via fence. A
    // finite ground leaves bare substrate outside its edge where the fields spread
    // like a microstrip's, so the ground edge gets the microstrip wall clearance.
    _coplanar_auto_domain_width(trace_span) {
        if (this.coplanar_gnd_width === null) {
            const active_width = trace_span + 2 * (this.gap + this.via_gap + this.w / 2);
            return Math.max(active_width * 1.5, this.h * 10, GCPW_APERTURE_WIDTHS * (trace_span + 2 * this.gap));
        }
        const active_width = trace_span + 2 * (this.gap + this.coplanar_gnd_width);
        return Math.max(active_width * 1.5, active_width + 2 * Math.max(8 * this.w, 15 * this.h));
    }

    _calculate_coordinates() {
        // Bottom extension for cut ground
        this.y_ext_start = 0;
        this.y_ext_end = this.gnd_cut_sub_h;

        // New bottom ground plane location
        this.y_gnd_bot_start = this.y_ext_end;
        this.y_gnd_bot_end = this.y_gnd_bot_start + this.t_gnd;
        if (this.gnd_cut_width === 0) {
            this.y_gnd_bot_end = this.y_gnd_bot_start;
        }

        this.y_sub_start = this.y_gnd_bot_end;
        this.y_sub_end = this.y_sub_start + this.h;

        // Top dielectric
        this.y_top_diel_start = this.y_sub_end;
        this.y_top_diel_end = this.y_top_diel_start + this.top_diel_h;

        // Trace position - starts at top of top dielectric (or substrate if no top diel)
        // Negative thickness means trace extends down into substrate
        this.y_trace_start = this.y_top_diel_start;
        this.y_trace_end = this.y_trace_start + this.t;

        // Solder mask extents. The mask sits on the exposed metal: for an embedded
        // trace (t < 0) the exposed face is the substrate surface, so the trace-top
        // mask starts there and the side bands have only the mask's own height.
        this.y_trace_top = Math.max(this.y_trace_start, this.y_trace_end);
        this.sm_side_h = this.y_trace_top - this.y_trace_start + this.sm_t_trace;

        this.y_top_start = this.y_top_diel_end;

        // Top air/dielectric region
        if (this.enclosure_height !== null) {
            // Use enclosure height (distance from highest dielectric to top of domain)
            // Highest dielectric is y_top_start
            this.top_dielectric_h = this.enclosure_height;
        } else {
            this.top_dielectric_h = (this.h + this.t) * 15;
        }
        // The top ground follows the boundary condition. With auto height the
        // lid sits at the default auto-padded domain top instead.
        this.has_top_gnd = (this.boundaries[2] === "gnd");

        this.y_top_end = this.y_top_start + this.top_dielectric_h;

        if (this.has_top_gnd) {
            this.y_gnd_top_start = this.y_top_end;
            this.y_gnd_top_end = this.y_gnd_top_start + this.t_gnd;
            this.domain_height = this.y_gnd_top_end;
        } else {
            this.y_gnd_top_start = null;
            this.y_gnd_top_end = null;
            this.domain_height = this.y_top_end;
        }
    }

    _calculate_coplanar_geometry_x() {
        // Calculate x-coordinates for gaps, top grounds, vias
        // Handle both single-ended and differential layouts
        // Geometry is centered at x=0

        if (this.is_differential) {
            // Differential: two traces with spacing between
            // Layout: Via|TopGnd|Gap|Sig(-)|Space|Sig(+)|Gap|TopGnd|Via
            const half_spacing = this.trace_spacing / 2;

            // Left trace (negative polarity)
            this.x_tr_left_l = -this.w - half_spacing;
            this.x_tr_left_r = -half_spacing;

            // Right trace (positive polarity)
            this.x_tr_right_l = half_spacing;
            this.x_tr_right_r = this.w + half_spacing;

            // Outer gaps (from outer edges of traces)
            this.x_gap_outer_l = this.x_tr_left_l - this.gap;
            this.x_gap_outer_r = this.x_tr_right_r + this.gap;

            // Via positions (via_gap is distance from ground edge to via edge)
            this.via_x_left_inner = this.x_gap_outer_l - this.via_gap;
            this.via_x_right_inner = this.x_gap_outer_r + this.via_gap;
        } else {
            // Single-ended: one trace centered at x=0
            this.x_tr_l = -this.w / 2;
            this.x_tr_r = this.w / 2;

            // Gaps from signal to top ground
            this.x_gap_l = this.x_tr_l - this.gap;
            this.x_gap_r = this.x_tr_r + this.gap;

            // Via positions (via_gap is distance from ground edge to via edge)
            this.via_x_left_inner = this.x_gap_l - this.via_gap;
            this.via_x_right_inner = this.x_gap_r + this.via_gap;
        }

        // Outer ground edges and the via fence's outer edge. Full-width grounds
        // and their via fences run to the domain wall.
        const x_gap_l = this.is_differential ? this.x_gap_outer_l : this.x_gap_l;
        const x_gap_r = this.is_differential ? this.x_gap_outer_r : this.x_gap_r;
        if (this.coplanar_gnd_width === null) {
            this.x_gnd_outer_l = -this.domain_width / 2;
            this.x_gnd_outer_r = this.domain_width / 2;
            this.via_x_left_outer = this.x_gnd_outer_l;
            this.via_x_right_outer = this.x_gnd_outer_r;
        } else {
            this.x_gnd_outer_l = x_gap_l - this.coplanar_gnd_width;
            this.x_gnd_outer_r = x_gap_r + this.coplanar_gnd_width;
            const fence_w = this.coplanar_gnd_width - 2 * this.via_gap;
            this.via_x_left_outer = this.via_x_left_inner - fence_w;
            this.via_x_right_outer = this.via_x_right_inner + fence_w;
        }
    }

    _build_geometry_lists() {
        const dielectrics = [];
        const conductors = [];

        // Geometry is centered at x=0
        const xl = -this.w / 2;
        const xr = this.w / 2;
        const x_min = -this.domain_width / 2;
        const x_max = this.domain_width / 2;

        // Substrate (covers both cutout extension and main substrate)
        if (this.gnd_cut_sub_h > 0) {
            dielectrics.push(new Dielectric(
                x_min, this.y_ext_start,
                this.domain_width, this.y_trace_start - this.y_ext_start,
                this.er, this.tan_delta
            ));
        } else {
            dielectrics.push(new Dielectric(
                x_min, this.y_sub_start,
                this.domain_width, this.h,
                this.er, this.tan_delta
            ));
        }

        // Top dielectric (if present)
        if (this.top_diel_h > 0) {
            dielectrics.push(new Dielectric(
                x_min, this.y_top_diel_start,
                this.domain_width, this.top_diel_h,
                this.top_diel_er, this.top_diel_tand
            ));
        }

        // Top air/dielectric region
        dielectrics.push(new Dielectric(
            x_min, this.y_top_start,
            this.domain_width, this.top_dielectric_h,
            this.er_top, this.tand_top
        ));

        // Solder mask regions (overwrites previous)
        if (this.use_sm) {
            const n0 = dielectrics.length;
            if (this.use_coplanar_gnd) {
                // Coplanar solder mask: in gaps between signals and grounds
                this._add_coplanar_solder_mask(dielectrics);
            } else {
                // Standard microstrip solder mask
                this._add_standard_solder_mask(dielectrics, xl, xr, x_min, x_max);
            }
            // Thin sheets: the mesher brackets their faces regardless of how they
            // compare to the conductor dimensions.
            for (let k = n0; k < dielectrics.length; k++) dielectrics[k].thin_sheet = true;
        }

        // Conductors

        // Bottom ground (beneath everything)
        if (this.t_gnd > 0) {
            conductors.push(new Conductor(
                x_min, -this.t_gnd,
                this.domain_width, this.t_gnd,
                false
            ));
        }

        // Bottom ground plane (above cutout extension)
        if (this.gnd_cut_width === 0) {
            // No cutout - full ground plane
            if (this.y_gnd_bot_end > this.y_gnd_bot_start) {
                conductors.push(new Conductor(
                    x_min, this.y_gnd_bot_start,
                    this.domain_width, this.t_gnd,
                    false
                ));
            }
        } else {
            // With cutout - ground on sides only
            const cut_l = -this.gnd_cut_width / 2;
            const cut_r = this.gnd_cut_width / 2;

            // Left ground
            if (cut_l > x_min) {
                conductors.push(new Conductor(
                    x_min, this.y_gnd_bot_start,
                    cut_l - x_min, this.t_gnd,
                    false
                ));
            }

            // Right ground
            if (cut_r < x_max) {
                conductors.push(new Conductor(
                    cut_r, this.y_gnd_bot_start,
                    x_max - cut_r, this.t_gnd,
                    false
                ));
            }
        }

        // Coplanar vias through substrate (if enabled)
        if (this.use_coplanar_gnd && this.use_vias) {
            // Vias should extend from bottom ground to top coplanar grounds
            // Start from top of bottom ground (y_ext_start = t_gnd)
            // End at top of coplanar grounds (max of trace start/end for negative thickness)
            const via_y_start = this.y_ext_start;
            const via_y_end = Math.max(this.y_trace_start, this.y_trace_end);
            const via_height = via_y_end - this.y_ext_start;

            if (this.via_x_left_inner > this.via_x_left_outer) {
                conductors.push(new Conductor(
                    this.via_x_left_outer, via_y_start,
                    this.via_x_left_inner - this.via_x_left_outer, via_height,
                    false
                ));
            }
            if (this.via_x_right_inner < this.via_x_right_outer) {
                conductors.push(new Conductor(
                    this.via_x_right_inner, via_y_start,
                    this.via_x_right_outer - this.via_x_right_inner, via_height,
                    false
                ));
            }
        }

        // Signal trace(s)
        if (this.is_differential) {
            // Left trace (negative in odd mode, polarity = -1)
            const xl_left = -this.w - this.trace_spacing / 2;
            conductors.push(new Conductor(
                xl_left, this.y_trace_start,
                this.w, this.t,
                true, -1, this.plating
            ));
            // Right trace (positive in odd mode, polarity = +1)
            const xl_right = this.trace_spacing / 2;
            conductors.push(new Conductor(
                xl_right, this.y_trace_start,
                this.w, this.t,
                true, 1, this.plating
            ));
        } else {
            // Single trace (polarity = +1)
            conductors.push(new Conductor(
                xl, this.y_trace_start,
                this.w, this.t,
                true, 1, this.plating
            ));
        }

        // Coplanar grounds on the trace layer, from the gap edge out to the ground
        // edge (the domain wall for full-width grounds)
        if (this.use_coplanar_gnd) {
            const x_gap_l = this.is_differential ? this.x_gap_outer_l : this.x_gap_l;
            const x_gap_r = this.is_differential ? this.x_gap_outer_r : this.x_gap_r;
            conductors.push(new Conductor(
                this.x_gnd_outer_l, this.y_trace_start,
                x_gap_l - this.x_gnd_outer_l, this.t,
                false, 0, this.plating
            ));
            conductors.push(new Conductor(
                x_gap_r, this.y_trace_start,
                this.x_gnd_outer_r - x_gap_r, this.t,
                false, 0, this.plating
            ));
        }

        // Top ground plane (if present, for stripline)
        if (this.has_top_gnd) {
            conductors.push(new Conductor(
                x_min, this.y_gnd_top_start,
                this.domain_width, this.t_gnd,
                false
            ));
        }

        // Side ground planes (based on boundaries array)
        const side_gnd_thickness = this.t_gnd;
        const side_gnd_height = this.has_top_gnd ?
            (this.y_gnd_top_start + this.t_gnd) :
            (this.y_top_start + this.top_dielectric_h);

        // Left side ground (if boundaries[0] === "gnd")
        if (this.boundaries[0] === "gnd") {
            conductors.push(new Conductor(
                x_min, -this.t_gnd,
                side_gnd_thickness, side_gnd_height + this.t_gnd,
                false
            ));
        }

        // Right side ground (if boundaries[1] === "gnd")
        if (this.boundaries[1] === "gnd") {
            conductors.push(new Conductor(
                x_max - side_gnd_thickness, -this.t_gnd,
                side_gnd_thickness, side_gnd_height + this.t_gnd,
                false
            ));
        }

        return [dielectrics, conductors];
    }

    // Side-coat width on a face looking across a gap at another conductor face
    _sm_side_in(gap) {
        return Math.min(this.sm_t_side, gap / 2);
    }

    _add_standard_solder_mask(dielectrics, xl, xr, x_min, x_max) {
        // Standard microstrip solder mask: the substrate between and beside the traces
        // (stopping at the side masks, so the two do not overlap), then each trace's
        // side and top masks.
        const sm = (x, y, w, h) => dielectrics.push(new Dielectric(x, y, w, h, this.sm_er, this.sm_tand));
        const half_spacing = this.trace_spacing / 2;
        const traces = this.is_differential
            ? [[-this.w - half_spacing, -half_spacing], [half_spacing, this.w + half_spacing]]
            : [[xl, xr]];
        let start = x_min;
        for (const [l, r] of traces) {
            const end = l - this.sm_t_side;
            if (end > start) sm(start, this.y_sub_end, end - start, this.sm_t_sub);
            start = r + this.sm_t_side;
        }
        if (start < x_max) sm(start, this.y_sub_end, x_max - start, this.sm_t_sub);
        // Side coats facing each other across a gap narrower than two coats meet at
        // the gap centre instead of overlapping into slivers.
        const inner = this._sm_side_in(this.trace_spacing);
        traces.forEach(([l, r], k) => {
            const sl = k > 0 ? inner : this.sm_t_side, sr = k < traces.length - 1 ? inner : this.sm_t_side;
            sm(l - sl, this.y_trace_start, sl, this.sm_side_h);
            sm(r, this.y_trace_start, sr, this.sm_side_h);
            sm(l, this.y_trace_top, this.w, this.sm_t_trace);
        });
    }

    _add_coplanar_solder_mask(dielectrics) {
        // Coplanar solder mask: covers gaps and tops of conductors

        if (this.is_differential) {
            // Differential coplanar solder mask
            const xl = this.x_tr_left_l;
            const xr_left = this.x_tr_left_r;
            const xl_right = this.x_tr_right_l;
            const xr = this.x_tr_right_r;
            const xl_gap = this.x_gap_outer_l;
            const xr_gap = this.x_gap_outer_r;

            // Solder mask on substrate in outer gaps
            const xl_sub_start = xl_gap + this.sm_t_side;
            const xl_sub_end = xl - this.sm_t_side;
            if (xl_sub_end > xl_sub_start) {
                dielectrics.push(new Dielectric(
                    xl_sub_start, this.y_sub_end,
                    xl_sub_end - xl_sub_start, this.sm_t_sub,
                    this.sm_er, this.sm_tand
                ));
            }

            const xr_sub_start = xr + this.sm_t_side;
            const xr_sub_end = xr_gap - this.sm_t_side;
            if (xr_sub_end > xr_sub_start) {
                dielectrics.push(new Dielectric(
                    xr_sub_start, this.y_sub_end,
                    xr_sub_end - xr_sub_start, this.sm_t_sub,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask in center gap (between traces)
            const center_start = xr_left + this.sm_t_side;
            const center_end = xl_right - this.sm_t_side;
            if (center_end > center_start) {
                dielectrics.push(new Dielectric(
                    center_start, this.y_sub_end,
                    center_end - center_start, this.sm_t_sub,
                    this.sm_er, this.sm_tand
                ));
            }

            // Side coats, clipped to half of each gap so facing coats meet
            // instead of overlapping
            const s_out_l = this._sm_side_in(xl - xl_gap), s_out_r = this._sm_side_in(xr_gap - xr);
            const s_in = this._sm_side_in(xl_right - xr_left);

            // Solder mask on sides of left trace
            dielectrics.push(new Dielectric(
                xl - s_out_l, this.y_trace_start,
                s_out_l, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));
            dielectrics.push(new Dielectric(
                xr_left, this.y_trace_start,
                s_in, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));

            // Solder mask on sides of right trace
            dielectrics.push(new Dielectric(
                xl_right - s_in, this.y_trace_start,
                s_in, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));
            dielectrics.push(new Dielectric(
                xr, this.y_trace_start,
                s_out_r, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));

            // Solder mask on outer gap sides (ground side)
            dielectrics.push(new Dielectric(
                xl_gap, this.y_trace_start,
                s_out_l, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));
            dielectrics.push(new Dielectric(
                xr_gap - s_out_r, this.y_trace_start,
                s_out_r, this.sm_side_h,
                this.sm_er, this.sm_tand
            ));

            // Solder mask on top of traces
            dielectrics.push(new Dielectric(
                xl, this.y_trace_top,
                this.w, this.sm_t_trace,
                this.sm_er, this.sm_tand
            ));
            dielectrics.push(new Dielectric(
                xl_right, this.y_trace_top,
                this.w, this.sm_t_trace,
                this.sm_er, this.sm_tand
            ));

            this._add_coplanar_gnd_solder_mask(dielectrics, xl_gap, xr_gap);
        } else {
            // Single-ended coplanar solder mask
            const xl = this.x_tr_l;
            const xr = this.x_tr_r;
            const xl_gap = this.x_gap_l;
            const xr_gap = this.x_gap_r;

            // Side coats, clipped to half of each gap so the trace and ground
            // coats meet instead of overlapping
            const s_l = this._sm_side_in(xl - xl_gap), s_r = this._sm_side_in(xr_gap - xr);

            // Trace side positions
            const xsl = xl - s_l;
            const xsr = xr + s_r;

            // Ground side mask positions
            const xl_gnd_side_end = xl_gap + s_l;
            const xr_gnd_side_start = xr_gap - s_r;

            // Solder mask on substrate in gaps (between grounds and signal)
            // Left gap
            const xl_sub_start = xl_gnd_side_end;
            const xl_sub_end = Math.min(xl, Math.max(xl_sub_start, xsl));
            if (xl_sub_end > xl_sub_start) {
                dielectrics.push(new Dielectric(
                    xl_sub_start, this.y_sub_end,
                    xl_sub_end - xl_sub_start, this.sm_t_sub,
                    this.sm_er, this.sm_tand
                ));
            }

            // Right gap
            const xr_sub_end = xr_gnd_side_start;
            const xr_sub_start_calc = Math.max(xr, Math.min(xr_sub_end, xsr));
            if (xr_sub_end > xr_sub_start_calc) {
                dielectrics.push(new Dielectric(
                    xr_sub_start_calc, this.y_sub_end,
                    xr_sub_end - xr_sub_start_calc, this.sm_t_sub,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask on left side of trace
            if (xsl > -this.domain_width/2) {
                dielectrics.push(new Dielectric(
                    xsl, this.y_trace_start,
                    s_l, this.sm_side_h,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask on right side of trace
            if (xsr <= this.domain_width/2) {
                dielectrics.push(new Dielectric(
                    xr, this.y_trace_start,
                    s_r, this.sm_side_h,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask on ground side of left gap
            if (xl_gnd_side_end > xl_gap) {
                dielectrics.push(new Dielectric(
                    xl_gap, this.y_trace_start,
                    xl_gnd_side_end - xl_gap, this.sm_side_h,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask on ground side of right gap
            if (xr_gap > xr_gnd_side_start) {
                dielectrics.push(new Dielectric(
                    xr_gnd_side_start, this.y_trace_start,
                    xr_gap - xr_gnd_side_start, this.sm_side_h,
                    this.sm_er, this.sm_tand
                ));
            }

            // Solder mask on top of signal trace
            dielectrics.push(new Dielectric(
                xl, this.y_trace_top,
                this.w, this.sm_t_trace,
                this.sm_er, this.sm_tand
            ));

            this._add_coplanar_gnd_solder_mask(dielectrics, xl_gap, xr_gap);
        }
    }

    // Solder mask over the coplanar grounds: their top faces and, past a finite
    // ground's outer edge, the side band and the bare substrate out to the wall.
    _add_coplanar_gnd_solder_mask(dielectrics, xl_gap, xr_gap) {
        const push = (x, y, w, h) => {
            if (w > 0) dielectrics.push(new Dielectric(x, y, w, h, this.sm_er, this.sm_tand));
        };
        const gl = this.x_gnd_outer_l, gr = this.x_gnd_outer_r;
        push(gl, this.y_trace_top, xl_gap - gl, this.sm_t_trace);
        push(xr_gap, this.y_trace_top, gr - xr_gap, this.sm_t_trace);
        if (this.coplanar_gnd_width === null) return;
        const x_min = -this.domain_width / 2, x_max = this.domain_width / 2;
        push(gl - this.sm_t_side, this.y_trace_start, this.sm_t_side, this.sm_side_h);
        push(gr, this.y_trace_start, this.sm_t_side, this.sm_side_h);
        push(x_min, this.y_sub_end, gl - this.sm_t_side - x_min, this.sm_t_sub);
        push(gr + this.sm_t_side, this.y_sub_end, x_max - gr - this.sm_t_side, this.sm_t_sub);
    }
}

export { MicrostripSolver };
